import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Activity, CheckCircle2, Hourglass, Loader2 } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Skeleton } from '@/components/ui/skeleton';
import { useDisruptionProgress } from '@/lib/disruptions/use-disruptions';
import type { DisruptionProgress, RunStatus } from '@/lib/disruptions/types';
import { RosterBulkBar } from './roster-bulk-bar';
import { AutomaticAcceptancePanel } from './automatic-acceptance-panel';
import { lccApi } from '@/lib/lcc/api';

// disruption-roster-mvp-parity (epic NEX-369 / U2) — the roster action sidebar: the
// right column of the disruption Passengers tab, mirroring the MVP "SCL Severe
// Storm" BulkActionBar + EngineProgressPanel:
//
//   ┌──────────────────────────┐
//   │ GENERATE OPTIONS          │  ← RosterBulkBar (sidebar variant): generate-only
//   │  [ Generate options ]     │     (acceptTopRank:false) + accept-top-rank
//   │  [ Accept best option ]   │     (acceptTopRank:true) + View progress + lock
//   ├──────────────────────────┤
//   │ ENGINE PROGRESS           │  ← live progress panel (useDisruptionProgress)
//   │  ████████░░ 8/12 (67%)    │
//   ├──────────────────────────┤
//   │ AUTOMATIC ACCEPTANCE      │  ← AutomaticAcceptancePanel (U3): quality / cap /
//   └──────────────────────────┘     dry-run + SIMULATE / ACCEPT-AND-BOOK
//
// Realtime = WS, NO polling: the progress panel reads `useDisruptionProgress`, whose
// cache is seeded once on mount then pushed live over the disruption WS room the
// detail route joined (applyEvent → setQueryData; staleTime Infinity, no
// refetchInterval). The trigger + lock machinery is the existing, tested
// `RosterBulkBar` reused in its `sidebar` variant, so the disruption-scoped lock +
// live progress dialog are shared with (not duplicated from) the bar.
//
// U3 (M2) fills `automatic-acceptance-slot` with the rich AUTOMATIC ACCEPTANCE
// controls (quality rank / USD cap / dry-run / SIMULATE-ACCEPT) — the
// AutomaticAcceptancePanel driving the auto-rebook@v2 seam.

interface RosterActionSidebarProps {
  disruptionUrn: string;
}

export function RosterActionSidebar({ disruptionUrn }: RosterActionSidebarProps) {
  const { t } = useTranslation('roster');
  const { t: tLcc } = useTranslation('lcc');
  const providers = useQuery({
    queryKey: ['lcc', 'providers'],
    queryFn: lccApi.providers,
    retry: false,
  });
  const legacyAvailable = providers.isSuccess && providers.data.externalPurchaseOnly !== true;
  return (
    <aside
      className="flex w-full shrink-0 flex-col gap-4 overflow-y-auto lg:w-80"
      data-testid="roster-action-sidebar"
      aria-label={t('sidebar.label')}
    >
      {!legacyAvailable ? (
        <section
          className="border-border bg-card space-y-2 rounded-lg border p-3"
          data-testid="lcc-supervised-sidebar"
          role="status"
        >
          <h3 className="text-sm font-semibold">{tLcc('supervisedTitle')}</h3>
          <p className="text-muted-foreground text-sm">
            {providers.isPending
              ? tLcc('capabilityLoading')
              : providers.isError
                ? tLcc('capabilityFailed')
                : tLcc('supervisedSidebar')}
          </p>
          {providers.isSuccess ? (
            <p className="text-muted-foreground text-xs">{tLcc('candidateRules')}</p>
          ) : null}
        </section>
      ) : (
        <>
          {/* GENERATE OPTIONS + accept-top-rank + View progress + lock (shared dialog). */}
          <RosterBulkBar disruptionUrn={disruptionUrn} variant="sidebar" />

          {/* Live engine progress — WS-driven (no polling). */}
          <SidebarProgressPanel disruptionUrn={disruptionUrn} />

          {/* U3 (M2) — quality rank / USD cap / dry-run / SIMULATE-ACCEPT. Appended into
          U2's slot: the AutomaticAcceptancePanel drives the auto-rebook@v2 seam
          (dry-run preview one-shot; live run pushes progress over the WS panel above). */}
          <section
            className="border-border bg-card rounded-lg border p-3"
            data-testid="automatic-acceptance-slot"
            aria-label={t('sidebar.automaticAcceptance')}
          >
            <AutomaticAcceptancePanel disruptionUrn={disruptionUrn} />
          </section>
        </>
      )}
    </aside>
  );
}

/** Run-status tone (white-label tokens). The friendly labels live in i18n
 *  (`roster:progress.status.<RunStatus>`) — this file holds keys, never language. */
const RUN_STATUS_TONE: Record<RunStatus, string> = {
  NONE: 'text-muted-foreground',
  QUEUED: 'text-amber-600 dark:text-amber-400',
  RUNNING: 'text-sky-600 dark:text-sky-400',
  COMPLETED: 'text-emerald-600 dark:text-emerald-400',
  PARTIAL: 'text-amber-600 dark:text-amber-400',
  FAILED: 'text-destructive',
};

/**
 * The always-visible mini progress panel — the sidebar counterpart of the MVP
 * EngineProgressPanel. Reads the single WS-backed progress cache; the four UX
 * states are: loading (skeleton) · error (retry) · empty (no run yet) · success
 * (live bar + counts). NO polling — updates arrive over the WS via setQueryData.
 */
function SidebarProgressPanel({ disruptionUrn }: { disruptionUrn: string }) {
  const { t } = useTranslation('roster');
  const { data, isLoading, isError, refetch } = useDisruptionProgress(disruptionUrn);

  return (
    <div
      className="border-border bg-card flex flex-col gap-2 rounded-lg border p-3"
      data-testid="roster-progress-panel"
    >
      <h3 className="text-muted-foreground flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide">
        <Activity className="h-3.5 w-3.5" aria-hidden />
        {t('progress.title')}
      </h3>

      {isLoading ? (
        <div className="flex flex-col gap-2" data-testid="roster-progress-loading" aria-busy="true">
          <Skeleton className="h-3 w-24" />
          <Skeleton className="h-1.5 w-full" />
        </div>
      ) : isError || !data ? (
        <div
          className="flex flex-wrap items-center justify-between gap-2 text-xs"
          data-testid="roster-progress-error"
        >
          <span className="text-muted-foreground">{t('progress.loadError')}</span>
          <button
            type="button"
            onClick={() => void refetch()}
            className="border-input bg-background text-foreground hover:bg-muted rounded-md border px-2 py-1 font-medium"
            data-testid="roster-progress-retry"
          >
            {t('actions.retry')}
          </button>
        </div>
      ) : (
        <ProgressBody data={data} />
      )}
    </div>
  );
}

function ProgressBody({ data }: { data: DisruptionProgress }) {
  const { t } = useTranslation('roster');
  // Prefer the run's own group counters (run-synchronous) and fall back to the S4
  // rollup so the bar is honest before/after a run.
  const total = data.plannedGroups ?? data.groupsTotal ?? 0;
  const done = data.processedGroups ?? data.resolved ?? 0;
  const noRun = data.runStatus === 'NONE' && total === 0 && data.optionsGenerated === 0;

  // Empty state: no run has produced anything yet.
  if (noRun) {
    return (
      <p
        className="text-muted-foreground flex items-center gap-1.5 text-xs"
        data-testid="roster-progress-empty"
      >
        <Hourglass className="h-3.5 w-3.5" aria-hidden />
        {t('progress.empty')}
      </p>
    );
  }

  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const running = data.runStatus === 'RUNNING' || data.runStatus === 'QUEUED';
  const complete = data.runStatus === 'COMPLETED';

  return (
    <div className="flex flex-col gap-2" data-testid="roster-progress-success">
      <div className="flex items-center justify-between text-[11px]">
        <span
          className={cn(
            'inline-flex items-center gap-1 font-medium',
            RUN_STATUS_TONE[data.runStatus],
          )}
        >
          {running ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden />
          ) : complete ? (
            <CheckCircle2 className="h-3 w-3" aria-hidden />
          ) : null}
          {t(`progress.status.${data.runStatus}`)}
        </span>
        <span className="text-muted-foreground tabular-nums">
          {done}/{total} <span className="ml-0.5">({pct}%)</span>
        </span>
      </div>

      {/* Contained, token-themed progress bar. */}
      <div
        className="bg-muted h-1.5 w-full overflow-hidden rounded-full"
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={t('progress.barLabel')}
      >
        <div
          className={cn(
            'h-full rounded-full transition-[width] duration-500',
            data.runStatus === 'FAILED' ? 'bg-destructive' : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>

      <div className="text-muted-foreground flex items-center justify-between text-[11px]">
        <span data-testid="roster-progress-options">
          {t('progress.withOptions', {
            value: data.optionsGenerated.toLocaleString(),
          })}
        </span>
        {(data.degradedGroups ?? 0) > 0 ? (
          <span
            className="text-amber-600 dark:text-amber-400"
            data-testid="roster-progress-degraded"
          >
            {t('progress.degraded', { n: data.degradedGroups ?? 0 })}
          </span>
        ) : null}
      </div>
    </div>
  );
}
